import {
  loadRestaurantDemoState,
  saveRestaurantDemoState,
  type RestaurantDemoCategory,
  type RestaurantDemoProduct,
  type RestaurantDemoState,
} from "../sales-playground/restaurant-sandbox";
import {
  createRestaurantDemoV5State,
  loadRestaurantDemoV5State,
  saveRestaurantDemoV5State,
  type RestaurantDemoV5State,
} from "../sales-playground/restaurant-demo-v5-state";

const category = (name: string, sortOrder: number): RestaurantDemoCategory => Object.freeze({
  id: `commercial-cat-${name.toLocaleLowerCase("es").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`,
  name,
  sortOrder,
  active: true,
});

const product = (
  id: string,
  name: string,
  categoryName: string,
  description: string,
  priceMinor: number,
  sortOrder: number,
  featured = false,
  tags: readonly string[] = [],
): RestaurantDemoProduct => Object.freeze({
  id,
  name,
  category: categoryName,
  description,
  priceMinor,
  available: true,
  featured,
  tags: Object.freeze(tags),
  active: true,
  sortOrder,
});

const REQUIRED_CATEGORIES: readonly RestaurantDemoCategory[] = Object.freeze([
  category("Entradas", 10),
  category("Pizzas", 20),
  category("Principales", 30),
  category("Pastas", 40),
  category("Postres", 50),
  category("Bebidas", 60),
  category("Cafetería", 70),
]);

const COMMERCIAL_PRODUCTS: readonly RestaurantDemoProduct[] = Object.freeze([
  product("prod-provoleta", "Provoleta al horno", "Entradas", "Provolone, tomates confitados y orégano.", 890000, 70, false, ["entrada", "queso"]),
  product("prod-papas-rusticas", "Papas rústicas", "Entradas", "Papas doradas, parmesano y alioli casero.", 650000, 80, false, ["entrada", "vegetariana"]),
  product("prod-croquetas", "Croquetas de jamón", "Entradas", "Cremosas por dentro, crocantes por fuera.", 740000, 90, false, ["entrada"]),

  product("prod-jamon-morrones", "Pizza Jamón y Morrones", "Pizzas", "Muzzarella, jamón cocido, morrones asados y aceitunas.", 1890000, 100, false, ["pizza"]),
  product("prod-cuatro-quesos", "Pizza Cuatro Quesos", "Pizzas", "Muzzarella, provolone, azul y parmesano.", 1950000, 110, true, ["pizza", "queso"]),
  product("prod-rucula-crudo", "Pizza Rúcula y Crudo", "Pizzas", "Jamón crudo, rúcula fresca, parmesano y oliva.", 2050000, 120, false, ["pizza"]),
  product("prod-pepperoni", "Pizza Pepperoni", "Pizzas", "Muzzarella, salsa de tomate y pepperoni.", 1880000, 130, false, ["pizza"]),
  product("prod-vegetales", "Pizza Vegetales Asados", "Pizzas", "Berenjena, zucchini, morrón, cebolla y muzzarella.", 1790000, 140, false, ["pizza", "vegetariana"]),

  product("prod-milanesa-napo", "Milanesa Napolitana", "Principales", "Carne vacuna, salsa de tomate, jamón, muzzarella y papas.", 1890000, 150, true, ["principal", "carne"]),
  product("prod-milanesa-casa", "Milanesa de la Casa", "Principales", "Milanesa, provolone, cebolla caramelizada y papas rústicas.", 2050000, 160, false, ["principal", "carne"]),
  product("prod-lomo", "Lomo Grillado", "Principales", "Lomo, vegetales de estación y reducción de vino tinto.", 2490000, 170, true, ["principal", "carne"]),
  product("prod-suprema", "Suprema Grillada", "Principales", "Pechuga grillada, vegetales asados y limón.", 1790000, 180, false, ["principal", "pollo"]),
  product("prod-risotto", "Risotto de Hongos", "Principales", "Arroz cremoso, hongos, parmesano y oliva.", 1850000, 190, false, ["principal", "vegetariana"]),
  product("prod-salmon", "Salmón con Vegetales", "Principales", "Salmón grillado, vegetales y manteca cítrica.", 2650000, 200, false, ["principal", "pescado"]),

  product("prod-sorrentinos", "Sorrentinos de Jamón y Queso", "Pastas", "Pasta rellena artesanal, salsa a elección.", 1650000, 210, true, ["pasta"]),
  product("prod-ravioles", "Ravioles de Verdura", "Pastas", "Espinaca y ricota, salsa a elección.", 1590000, 220, false, ["pasta", "vegetariana"]),
  product("prod-noquis", "Ñoquis de Papa", "Pastas", "Ñoquis caseros, salsa a elección.", 1490000, 230, false, ["pasta", "vegetariana"]),
  product("prod-tagliatelle", "Tagliatelle Caseros", "Pastas", "Cintas de pasta fresca, salsa a elección.", 1690000, 240, false, ["pasta"]),

  product("prod-tiramisu", "Tiramisú", "Postres", "Mascarpone, café y cacao.", 650000, 250, true, ["postre"]),
  product("prod-cheesecake", "Cheesecake de Frutos Rojos", "Postres", "Cheesecake cremoso con salsa de frutos rojos.", 690000, 260, false, ["postre"]),
  product("prod-brownie", "Brownie Tibio", "Postres", "Chocolate, nueces y helado artesanal.", 630000, 270, false, ["postre"]),
  product("prod-helado", "Helado Artesanal", "Postres", "Dos bochas a elección.", 490000, 280, false, ["postre"]),

  product("prod-agua", "Agua Mineral", "Bebidas", "Con o sin gas, 500 ml.", 250000, 290, false, ["bebida"]),
  product("prod-gaseosa", "Gaseosa", "Bebidas", "Línea regular o sin azúcar.", 290000, 300, false, ["bebida"]),
  product("prod-limonada", "Limonada de la Casa", "Bebidas", "Limón fresco, menta y jengibre.", 420000, 310, true, ["bebida", "sin alcohol"]),
  product("prod-cerveza", "Cerveza Tirada", "Bebidas", "Pinta de cerveza artesanal.", 480000, 320, false, ["bebida"]),
  product("prod-vino-copa", "Copa de Vino", "Bebidas", "Selección de la casa.", 550000, 330, false, ["bebida"]),
  product("prod-vino", "Vino de la Casa", "Bebidas", "Botella 750 ml, selección argentina.", 1290000, 340, false, ["bebida"]),

  product("prod-espresso", "Espresso", "Cafetería", "Café espresso de molienda fresca.", 240000, 350, false, ["cafe"]),
  product("prod-cafe-leche", "Café con Leche", "Cafetería", "Espresso doble y leche vaporizada.", 320000, 360, false, ["cafe"]),
  product("prod-cappuccino", "Cappuccino", "Cafetería", "Espresso, leche cremosa y cacao.", 370000, 370, true, ["cafe"]),
  product("prod-te", "Té en Hebras", "Cafetería", "Selección de té negro, verde o herbal.", 300000, 380, false, ["te"]),
  product("prod-medialunas", "Medialunas", "Cafetería", "Dos medialunas de manteca recién horneadas.", 280000, 390, false, ["cafeteria"]),
  product("prod-tostado", "Tostado de Jamón y Queso", "Cafetería", "Pan dorado, jamón cocido y queso.", 590000, 400, false, ["cafeteria"]),
]);

function normalizeCategoryName(name: string): string {
  return name === "Especialidades" ? "Principales" : name;
}

export function ensureRestaurantCommercialCatalog(storageSlug: string, businessName: string): RestaurantDemoState {
  const current = loadRestaurantDemoState(storageSlug, businessName);
  const categoryByName = new Map<string, RestaurantDemoCategory>();
  for (const existing of current.categories) {
    const name = normalizeCategoryName(existing.name);
    categoryByName.set(name, Object.freeze({ ...existing, name }));
  }
  for (const required of REQUIRED_CATEGORIES) {
    const existing = categoryByName.get(required.name);
    categoryByName.set(required.name, existing ? Object.freeze({ ...existing, sortOrder: required.sortOrder, active: true }) : required);
  }
  const knownNames = new Set(REQUIRED_CATEGORIES.map((item) => item.name));
  const categories = Object.freeze([
    ...REQUIRED_CATEGORIES.map((item) => categoryByName.get(item.name)!),
    ...[...categoryByName.values()].filter((item) => !knownNames.has(item.name)).sort((a, b) => a.sortOrder - b.sortOrder),
  ]);

  const normalizedExisting = current.products.map((item) => item.category === "Especialidades" ? Object.freeze({ ...item, category: "Principales" }) : item);
  const ids = new Set(normalizedExisting.map((item) => item.id));
  const products = Object.freeze([...normalizedExisting, ...COMMERCIAL_PRODUCTS.filter((item) => !ids.has(item.id))]);
  const next = Object.freeze({ ...current, businessName, categories, products });
  saveRestaurantDemoState(storageSlug, next);
  return next;
}

export function ensureRestaurantCommercialV5(storageSlug: string, state: RestaurantDemoState): RestaurantDemoV5State {
  const current = loadRestaurantDemoV5State(storageSlug, state);
  const fallback = createRestaurantDemoV5State(state);
  const modifierIds = new Set(current.modifierGroups.map((item) => item.id));
  const visualIds = new Set(current.productVisuals.map((item) => item.productId));
  const recipeIds = new Set(current.recipes.map((item) => item.productId));
  const next = Object.freeze({
    ...current,
    modifierGroups: Object.freeze([...current.modifierGroups, ...fallback.modifierGroups.filter((item) => !modifierIds.has(item.id))]),
    productVisuals: Object.freeze([...current.productVisuals, ...fallback.productVisuals.filter((item) => !visualIds.has(item.productId))]),
    recipes: Object.freeze([...current.recipes, ...fallback.recipes.filter((item) => !recipeIds.has(item.productId))]),
  });
  saveRestaurantDemoV5State(storageSlug, next);
  return next;
}
